import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@utils/cn';

/**
 * Heading variants using cva for consistent styling across all heading levels
 */
const headingVariants = cva('font-display font-bold tracking-tight', {
  variants: {
    level: {
      h1: 'text-4xl md:text-5xl lg:text-6xl',
      h2: 'text-3xl md:text-4xl lg:text-5xl',
      h3: 'text-2xl md:text-3xl lg:text-4xl',
      h4: 'text-xl md:text-2xl lg:text-3xl',
      h5: 'text-lg md:text-xl lg:text-2xl',
      h6: 'text-base md:text-lg lg:text-xl',
    },
  },
  defaultVariants: {
    level: 'h1',
  },
});

/**
 * Text variants for body, caption, and label text
 */
const textVariants = cva('font-sans', {
  variants: {
    variant: {
      body: 'text-base leading-relaxed',
      bodySmall: 'text-sm leading-relaxed',
      bodyLarge: 'text-lg leading-relaxed',
      caption: 'text-xs text-muted-foreground',
      label:
        'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
    },
  },
  defaultVariants: {
    variant: 'body',
  },
});

/**
 * H1 Component - Primary page heading
 */
export type H1Props = React.HTMLAttributes<HTMLHeadingElement>;

export const H1 = React.forwardRef<HTMLHeadingElement, H1Props>(({ className, ...props }, ref) => (
  <h1 ref={ref} className={cn(headingVariants({ level: 'h1' }), className)} {...props} />
));
H1.displayName = 'H1';

/**
 * H2 Component - Section heading
 */
export type H2Props = React.HTMLAttributes<HTMLHeadingElement>;

export const H2 = React.forwardRef<HTMLHeadingElement, H2Props>(({ className, ...props }, ref) => (
  <h2 ref={ref} className={cn(headingVariants({ level: 'h2' }), className)} {...props} />
));
H2.displayName = 'H2';

/**
 * H3 Component - Subsection heading
 */
export type H3Props = React.HTMLAttributes<HTMLHeadingElement>;

export const H3 = React.forwardRef<HTMLHeadingElement, H3Props>(({ className, ...props }, ref) => (
  <h3 ref={ref} className={cn(headingVariants({ level: 'h3' }), className)} {...props} />
));
H3.displayName = 'H3';

/**
 * H4 Component
 */
export type H4Props = React.HTMLAttributes<HTMLHeadingElement>;

export const H4 = React.forwardRef<HTMLHeadingElement, H4Props>(({ className, ...props }, ref) => (
  <h4 ref={ref} className={cn(headingVariants({ level: 'h4' }), className)} {...props} />
));
H4.displayName = 'H4';

/**
 * H5 Component
 */
export type H5Props = React.HTMLAttributes<HTMLHeadingElement>;

export const H5 = React.forwardRef<HTMLHeadingElement, H5Props>(({ className, ...props }, ref) => (
  <h5 ref={ref} className={cn(headingVariants({ level: 'h5' }), className)} {...props} />
));
H5.displayName = 'H5';

/**
 * H6 Component
 */
export type H6Props = React.HTMLAttributes<HTMLHeadingElement>;

export const H6 = React.forwardRef<HTMLHeadingElement, H6Props>(({ className, ...props }, ref) => (
  <h6 ref={ref} className={cn(headingVariants({ level: 'h6' }), className)} {...props} />
));
H6.displayName = 'H6';

/**
 * Body Component - Standard body text
 */
export type BodyProps = React.HTMLAttributes<HTMLParagraphElement>;

export const Body = React.forwardRef<HTMLParagraphElement, BodyProps>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn(textVariants({ variant: 'body' }), className)} {...props} />
  )
);
Body.displayName = 'Body';

/**
 * BodySmall Component - Smaller body text
 */
export type BodySmallProps = React.HTMLAttributes<HTMLParagraphElement>;

export const BodySmall = React.forwardRef<HTMLParagraphElement, BodySmallProps>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn(textVariants({ variant: 'bodySmall' }), className)} {...props} />
  )
);
BodySmall.displayName = 'BodySmall';

/**
 * BodyLarge Component - Larger body text for emphasis
 */
export type BodyLargeProps = React.HTMLAttributes<HTMLParagraphElement>;

export const BodyLarge = React.forwardRef<HTMLParagraphElement, BodyLargeProps>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn(textVariants({ variant: 'bodyLarge' }), className)} {...props} />
  )
);
BodyLarge.displayName = 'BodyLarge';

/**
 * Caption Component - Small secondary text
 */
export type CaptionProps = React.HTMLAttributes<HTMLParagraphElement>;

export const Caption = React.forwardRef<HTMLParagraphElement, CaptionProps>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn(textVariants({ variant: 'caption' }), className)} {...props} />
  )
);
Caption.displayName = 'Caption';

/**
 * Label Component - Form field labels
 */
export type LabelProps = React.LabelHTMLAttributes<HTMLLabelElement>;

export const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, ...props }, ref) => (
    <label ref={ref} className={cn(textVariants({ variant: 'label' }), className)} {...props} />
  )
);
Label.displayName = 'Label';

export { headingVariants, textVariants };
